import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';

import { hasPublicLinksOperation } from '../utils/hasPublicLinksOperation.ts';

import type {
	usePublicLinkContentQueryPublicLinkContentQuery as usePublicLinkContentQueryPublicLinkContentQuery$data,
	usePublicLinkContentQueryPublicLinkContentQueryVariables as usePublicLinkContentQueryPublicLinkContentQuery$variables,
} from './__types__/usePublicLinkContentQueryPublicLinkContentQuery.ts';
export type PublicLinkContentData = {
	contentType: string;
	canEditPublicLink: boolean;
	canUpdatePublicLink: boolean;
	canDeletePublicLink: boolean;
};

export type PublicLinkContentQueryData = {
	isLoading: boolean;
	error?: Error;
	data?: PublicLinkContentData;
	refetch?: () => void;
};

const computeData = (
	data: usePublicLinkContentQueryPublicLinkContentQuery$data | undefined,
	isExternalCollaborator: boolean,
): PublicLinkContentData => {
	const contentType = data?.content?.nodes?.[0]?.type || '';
	const operations = data?.content?.nodes?.[0]?.operations || [];
	const hasManagePublicLinksPermission = Boolean(data?.space?.currentUser?.isAdmin);
	const canEditContent = hasPublicLinksOperation(operations, 'update', contentType);
	const canCreatePublicLink = hasPublicLinksOperation(
		operations,
		'create_public_link',
		contentType,
	);
	const canUpdatePublicLink = hasPublicLinksOperation(operations, 'update', 'public_link');
	const canDeletePublicLink = hasPublicLinksOperation(operations, 'delete', 'public_link');

	const canEditPublicLink = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? canCreatePublicLink && !isExternalCollaborator
		: (hasManagePublicLinksPermission || canEditContent) && !isExternalCollaborator;

	return {
		contentType,
		canEditPublicLink,
		canUpdatePublicLink,
		canDeletePublicLink,
	};
};

export const usePublicLinkContentQuery = (props: {
	contentId: string;
	skip?: boolean;
}): PublicLinkContentQueryData => {
	const spaceKey = useSpaceKey();
	const { isExternalCollaborator } = useIsExternalCollaborator();
	const { loading, data, error, refetch } = useQueryInterop<
		usePublicLinkContentQueryPublicLinkContentQuery$data,
		usePublicLinkContentQueryPublicLinkContentQuery$variables
	>(
		graphql`
			query usePublicLinkContentQueryPublicLinkContentQuery(
				$contentId: ID!
				$spaceKey: String!
				$hasSpaceKey: Boolean!
			) {
				content(id: $contentId) {
					nodes {
						id
						type
						operations {
							operation
							targetType
						}
					}
				}
				space(key: $spaceKey) @include(if: $hasSpaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			skip: props.skip,
			variables: {
				contentId: props.contentId,
				hasSpaceKey: Boolean(spaceKey),
				spaceKey: spaceKey ?? '',
			},
		},
	);
	useEffect(() => {
		if (isUnauthorizedError(error)) markErrorAsHandled(error);
	}, [error]);

	return loading
		? { isLoading: true, refetch }
		: {
				isLoading: false,
				error,
				data: error ? undefined : computeData(data, isExternalCollaborator),
				refetch,
			};
};
